import React, { useState } from "react";
import { Modal, Spin, Button, Tooltip } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { groupBy } from "lodash";
import { l } from "../../../libs/localization";
import { translateRegistryOnView } from "../../../libs/utils";
import usePermissions from "../../../hooks/usePermissions";
import { getFeatures } from "../../../libs/features";
import CompanyGeneral from "../../company/company.general";
import CompanyHeader from "../../company/company.header";
import CompanyFinancial from "../../company/company.financial";
import CompanyInvestments from "../../company/company.investments";
import CreaList from "../../../components/List/Crea.list";
import EntityNews from "../../../components/EntityNews/EntityNews.component";

/**
 * DashboardCompanyColumn component
 * Receives company data as props to avoid multiple redundant fetches when used in a comparison dashboard.
 */
const DashboardCompanyColumn = ({
  id,
  columnIndex,
  data,
  schema,
  loading,
  sector,
  onRemove,
  section, // 'header', 'overview', 'financials', 'nielsen', 'mini-header', 'agencies', 'creativity', or 'acquisitions'
  selectedYear,
  nielsen,
  agencies,
  creas,
  entityNews,
  isExpanded,
  onToggleExpand,
}) => {
  const permissions = usePermissions();
  const features = getFeatures();
  const [selectedAlerts, setSelectedAlerts] = useState([]);

  const sectorModal = (title) => {
    /* ... sectorModal logic (no changes) ... */
    if (!data?.sector || !sector) return;

    data.sector.forEach((companySector, companySectorIndex) => {
      sector.forEach((sectorTree, sectorIndex) => {
        if (sectorTree.children) {
          sectorTree.children.forEach((childrenSector) => {
            if (childrenSector.children) {
              childrenSector.children.forEach((c) => {
                if (c._id === companySector._id)
                  data.sector[companySectorIndex].parent =
                    sector[sectorIndex].key;
              });
            }
            if (childrenSector._id === companySector._id)
              data.sector[companySectorIndex].parent = sector[sectorIndex].key;
          });
        }
      });
    });

    const groupedSector = groupBy(data.sector, "parent");
    Modal.info({
      title,
      content: (
        <>
          {Object.keys(groupedSector).map((parent) => {
            const parentObj = sector.find((s) => s.key === parent);
            return (
              <div key={parent}>
                {parent !== "undefined" && (
                  <p>
                    {parentObj
                      ? translateRegistryOnView(parentObj._id, "Sector") ||
                        parent
                      : parent}
                  </p>
                )}
                {parent !== "undefined" ? (
                  <ul>
                    {groupedSector[parent].map((s) => (
                      <li className="m-1" key={s._id}>
                        {translateRegistryOnView(s._id, "Sector") || s.key}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ul>
                    {groupedSector[parent].map((s) => (
                      <li className="m-1" key={s._id}>
                        {translateRegistryOnView(s.key, "Sector") || s.key}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </>
      ),
      width: "800px",
    });
  };

  const info = (title, content) => {
    Modal.info({
      title,
      content,
      width: "800px",
    });
  };

  if (loading) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "50px",
          minWidth: "300px",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return (
      <div
        style={{
          padding: "20px",
          minWidth: "300px",
          textAlign: "center",
        }}
      >
        <p>{l("COMMON.ERROR")}</p>
        <p>Company with ID {id} not found.</p>
      </div>
    );
  }

  if (section === "header") {
    return (
      <div style={{ position: "relative" }}>
        <div className="companyPage">
          <CompanyHeader
            data={data}
            permissions={permissions}
            match={{ params: { id } }}
            selectedAlerts={selectedAlerts}
            setSelectedAlerts={setSelectedAlerts}
            showRightSide={false}
          />
        </div>
        {onRemove && (
          <Tooltip title={l("COMMON.REMOVE")}>
            <Button
              type="primary"
              shape="circle"
              icon={<CloseOutlined style={{ fontSize: "16px" }} />}
              onClick={() => onRemove(id)}
              className="remove-company-button"
              style={{
                position: "absolute",
                top: "-10px",
                right: "-10px",
                zIndex: 5,
              }}
            />
          </Tooltip>
        )}
      </div>
    );
  }

  if (section === "overview") {
    return (
      <div className="companyPage">
        <CompanyGeneral
          k={id}
          data={data}
          entity="company"
          schema={schema}
          features={features}
          permissions={permissions}
          match={{ params: { id } }}
          sectorModal={sectorModal}
          info={info}
          columnLayout="stacked"
          visibleSections={["overview"]}
          hideTitle={true}
        />
      </div>
    );
  }

  if (section === "mini-header") {
    return (
      <div
        className="companyPage"
        style={{
          padding: "10px 15px",
          textAlign: "center",
          fontWeight: "800",
          fontSize: "0.85rem",
          color: "var(--primary)",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {data.name}
      </div>
    );
  }

  if (section === "financials") {
    return (
      <div className="companyPage">
        {data.financial_history && (
          <CompanyFinancial
            data={data.financial_history}
            hideGraph={true}
            hideTitle={true}
          />
        )}
      </div>
    );
  }

  if (section === "nielsen") {
    return (
      <CompanyInvestments
        companyId={id}
        selectedYear={selectedYear}
        hideTabs={true}
        mode="traditional"
        nielsenData={nielsen}
      />
    );
  }

  if (section === "agencies") {
    if (!agencies || agencies.length === 0) {
      return (
        <div
          className="companyPage"
          style={{ padding: "20px", textAlign: "center", color: "#999" }}
        >
          {l("PAGES.SEARCH.NO_DATA")}
        </div>
      );
    }

    // We take the first matched result that corresponds to our company
    const item = agencies[0];

    return (
      <div
        className="companyPage"
        style={{ padding: "15px", position: "relative" }}
      >
        <div
          className="agencies-list-container custom-scrollbar"
          style={{
            maxHeight: isExpanded ? "500px" : "180px",
            overflowY: isExpanded ? "auto" : "hidden",
            overflowX: "hidden",
          }}
        >
          {item.brands?.map((brand, bIndex) => (
            <div
              key={brand._id}
              style={{
                marginBottom: bIndex === item.brands.length - 1 ? 0 : "15px",
              }}
            >
              <div
                style={{
                  fontWeight: "bold",
                  borderBottom: "1px solid #eee",
                  marginBottom: "8px",
                  paddingBottom: "4px",
                  fontSize: "0.9rem",
                }}
              >
                {brand.name.toUpperCase()}
              </div>
              {brand.agencies.length > 0 ? (
                brand.agencies.map((agency) => (
                  <div
                    key={agency.id}
                    style={{ fontSize: "0.85rem", marginBottom: "4px" }}
                  >
                    {agency.categories.map((cat, cIndex) => (
                      <div
                        key={`${agency.id}-${cIndex}`}
                        style={{
                          display: "flex",
                          gap: "5px",
                          alignItems: "baseline",
                        }}
                      >
                        <span
                          style={{
                            color: "#888",
                            fontWeight: "500",
                            minWidth: "70px",
                          }}
                        >
                          {translateRegistryOnView(
                            cat.categoryId,
                            "Category",
                          ) || cat.categoryName}
                          :
                        </span>
                        <span
                          style={{ color: "var(--primary)", fontWeight: "600" }}
                        >
                          {agency.name.toUpperCase()}
                        </span>
                      </div>
                    ))}
                  </div>
                ))
              ) : (
                <div
                  style={{
                    fontSize: "0.8rem",
                    color: "#ccc",
                    fontStyle: "italic",
                  }}
                >
                  {l("PAGES.SEARCH.NO_DATA")}
                </div>
              )}
            </div>
          ))}
        </div>
        {!isExpanded && (
          <Tooltip title={l("COMMON.SHOW_MORE")}>
            <div
              onClick={onToggleExpand}
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "60px",
                background: "linear-gradient(transparent, rgba(255,255,255,1))",
                pointerEvents: "auto",
                cursor: "pointer",
                zIndex: 2,
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
                paddingBottom: "8px",
                color: "#999",
                fontSize: "1.5rem",
                fontWeight: "bold",
                letterSpacing: "2px",
                transition: "all 0.2s",
              }}
              className="agencies-expand-hover"
            >
              ...
            </div>
          </Tooltip>
        )}
      </div>
    );
  }

  if (section === "creativity") {
    return (
      <div className="companyPage" style={{ padding: "0 15px" }}>
        <CreaList data={creas} isDashboard={true} />
      </div>
    );
  }
  if (section === "acquisitions") {
    const displayNews = (entityNews || [])
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 3);

    return (
      <div
        className="companyPage"
        style={{
          padding: "15px",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          minHeight: "150px",
          justifyContent: displayNews.length === 0 ? "center" : "flex-start",
        }}
      >
        {displayNews.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              color: "#999",
              padding: "20px",
            }}
          >
            {l("PAGES.SEARCH.NO_DATA")}
          </div>
        ) : (
          displayNews.map((news) => <EntityNews key={news._id} news={news} />)
        )}
      </div>
    );
  }

  return null;
};

export default DashboardCompanyColumn;
